import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import styles from 'AppRoutes/SubscriptionReview/styles';
import SubscriptionOrderSummary from 'components/SubscriptionOrderSummary';
import { analyticsService } from 'services/analytics/analyticsService';
import { auth } from 'services/firebase/client';
import {
  CreateSubscriptionAccountError,
  createSubscriptionAccount,
} from 'services/subscriptionAccountService';
import {
  formatCurrencyBRL,
  parsePlanPrice,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';
import {
  formatPhoneMask,
  sanitizePhone,
  validatePhone,
} from 'utils/phoneValidation';
import {
  loadSubscriptionShippingPreview,
  buildPlanKey,
} from 'utils/subscriptionShippingPreview';
import { validateCPF } from 'utils/validation';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SubscriptionAccount = () => {
  const { selectedPlan, updateSubscription, updatePersonalData } =
    useSubscription();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const quantity = selectedPlan?.quantity || 1;
  const subtotal = useMemo(
    () => roundCurrency(parsePlanPrice(selectedPlan?.price) * quantity),
    [quantity, selectedPlan?.price],
  );

  const planKey = useMemo(() => buildPlanKey(selectedPlan), [selectedPlan]);
  const storedPreview = useMemo(
    () => (planKey ? loadSubscriptionShippingPreview(planKey) : null),
    [planKey],
  );
  const shippingValue = storedPreview?.quote.price ?? null;
  const total = sumCurrency(subtotal, shippingValue ?? 0);

  const packTitle = selectedPlan
    ? `${selectedPlan.label} - ${selectedPlan.type}`
    : '';

  useEffect(() => {
    analyticsService.trackPageView(
      '/subscription/account',
      'Cadastro da Assinatura',
    );
    analyticsService.trackSubscriptionFlow(
      'account',
      packTitle || undefined,
      subtotal,
    );
  }, [packTitle, subtotal]);

  useEffect(() => {
    if (!selectedPlan) {
      navigate('/');
      return;
    }
    if (auth.currentUser) {
      navigate('/subscription/address', { replace: true });
    }
  }, [navigate, selectedPlan]);

  const validate = useCallback(() => {
    const next: Record<string, string> = {};
    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
      next.email = 'E-mail inválido';
    }
    if (!fullName.trim() || fullName.trim().length < 3) {
      next.fullName = 'Informe o nome completo';
    }
    if (!validateCPF(cpf)) {
      next.cpf = 'CPF inválido';
    }
    if (!validatePhone(phone)) {
      next.phone = 'Telefone inválido. Informe DDD + número';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [cpf, email, fullName, phone]);

  const handleAdvance = useCallback(async () => {
    if (!selectedPlan) {
      return;
    }
    setFormError('');
    setEmailExists(false);
    if (!validate()) {
      return;
    }

    const planWithTotals = {
      ...selectedPlan,
      quantity,
      subtotal,
      total: subtotal,
    };
    updateSubscription(planWithTotals);

    setSubmitting(true);
    try {
      await createSubscriptionAccount({
        email: email.trim(),
        fullName: fullName.trim(),
        cpf,
        phoneNumber: phone,
        selectedPlan: planWithTotals,
      });

      updatePersonalData({
        email: email.trim().toLowerCase(),
        fullName: fullName.trim(),
        cpf: cpf.replace(/\D/g, ''),
        phoneNumber: formatPhoneMask(sanitizePhone(phone)),
      });

      analyticsService.trackCta(
        'subscription_account_submit',
        'Criar conta e continuar',
        { subscription_plan: packTitle || null },
      );
      analyticsService.trackSubscriptionFlow(
        'account_created',
        packTitle,
        subtotal,
      );

      navigate('/subscription/address');
    } catch (error) {
      if (error instanceof CreateSubscriptionAccountError) {
        if (error.code === 'email_exists') {
          setEmailExists(true);
          setErrors(prev => ({ ...prev, email: error.message }));
        } else if (error.code === 'invalid_cpf') {
          setErrors(prev => ({ ...prev, cpf: error.message }));
        } else if (error.code === 'invalid_phone') {
          setErrors(prev => ({ ...prev, phone: error.message }));
        } else if (error.code === 'invalid_email') {
          setErrors(prev => ({ ...prev, email: error.message }));
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError('Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    cpf,
    email,
    fullName,
    navigate,
    packTitle,
    phone,
    quantity,
    selectedPlan,
    subtotal,
    updatePersonalData,
    updateSubscription,
    validate,
  ]);

  if (!selectedPlan) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} sx={styles.gridContainer}>
        <Grid item xs={12} sm={6}>
          <Card sx={styles.card}>
            <CardContent>
              <Typography variant="h5" sx={{ ...styles.title, mb: 2 }}>
                Seus dados
              </Typography>
              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Usaremos seu e-mail para criar sua conta. Você receberá um link
                para definir a senha.
              </Typography> */}

              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}
              {emailExists && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Este e-mail já está cadastrado.{' '}
                  <Link
                    component={RouterLink}
                    to="/login"
                    state={{ from: '/subscription' }}
                  >
                    Ir para o login
                  </Link>
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setEmailExists(false);
                    }}
                    error={!!errors.email}
                    helperText={errors.email}
                    autoComplete="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome completo"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    autoComplete="name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputMask
                    mask="999.999.999-99"
                    value={cpf}
                    onChange={e => setCpf(e.target.value)}
                    maskChar={null}
                  >
                    {(
                      inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                    ): React.ReactElement => {
                      const {
                        size: _size,
                        color: _color,
                        ...rest
                      } = inputProps;
                      return (
                        <TextField
                          {...rest}
                          fullWidth
                          label="CPF"
                          error={!!errors.cpf}
                          helperText={errors.cpf}
                          inputProps={{ inputMode: 'numeric' }}
                        />
                      );
                    }}
                  </InputMask>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    placeholder="(00) 00000-0000"
                    value={phone}
                    onChange={e => setPhone(formatPhoneMask(e.target.value))}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    inputProps={{ inputMode: 'tel', maxLength: 15 }}
                    autoComplete="tel"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={styles.title}>
              {packTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subtotal R$ {formatCurrencyBRL(subtotal)}
            </Typography>
          </Box>
          <SubscriptionOrderSummary
            subtotal={subtotal}
            shippingValue={shippingValue}
            shippingPreview={storedPreview?.quote ?? null}
            total={total}
            onAdvance={() => {
              void handleAdvance();
            }}
            advanceDisabled={submitting}
            advanceLoading={submitting}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate('/subscription')}
        >
          Voltar
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionAccount;
